import {
  Observable,
  catchError,
  filter,
  from,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { Injectable, inject } from '@angular/core';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { ProfileState } from '../state/profile.state';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { Capacitor } from '@capacitor/core';
import { platformType } from '@et/typings';
import {
  TokenRefreshCoordinator,
  TokenRefreshUnavailableError,
} from '../token-refresh/token-refresh.coordinator';
import { useBetterAuthForCurrentPlatform } from '../auth-mode';

/**
 * Native app route guard. When `environment.useBetterAuth` is true, accepts a
 * valid OIDC access token (same contract as `JwtGuard` on web). Otherwise keeps
 * the legacy MSAL / Electron account checks.
 */
@Injectable({ providedIn: 'root' })
export class NativeJwtGuard {
  private router = inject(Router);
  private store = inject(Store);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private oauthService = inject(OAuthService, { optional: true });
  private tokenRefreshCoordinator = inject(TokenRefreshCoordinator, {
    optional: true,
  });

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (useBetterAuthForCurrentPlatform()) {
      return this.checkBetterAuth(state);
    }

    const platform = Capacitor.getPlatform();

    if (platform === platformType.ELECTRON) {
      return this.nativeUwp(state);
    } else {
      return this.webGuard(state);
    }
  }

  /**
   * A token that expired while the app was inactive is still recoverable via the
   * refresh token, so try that once before bouncing to `/auth/login` — otherwise a
   * navigation landing right on the expiry boundary would force a needless re-login.
   */
  private checkBetterAuth(state: RouterStateSnapshot): Observable<boolean> {
    if (
      this.oauthService?.hasValidAccessToken() ||
      this.oauthService?.hasValidIdToken()
    ) {
      return of(true);
    }
    return from(
      this.tokenRefreshCoordinator?.ensureValidToken() ??
        Promise.resolve(false),
    ).pipe(
      map((refreshed) => {
        if (refreshed) {
          return true;
        }
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }),
      // Issuer unreachable (offline/5xx): the session is intact, so don't bounce to
      // login — let the route load; the bearer interceptor retries once back online.
      catchError((error: unknown) =>
        error instanceof TokenRefreshUnavailableError
          ? of(true)
          : throwError(() => error),
      ),
    );
  }

  /**
   * This function is used to guard routes for native applications.
   * It checks if the user profile exists in the store.
   *
   * If the user profile exists, it allows the navigation by returning an Observable of true.
   *
   * If the user profile doesn't exist, it redirects the user to the login page and returns an Observable of false.
   * The URL of the current state is passed as a query parameter 'returnUrl' to the login page.
   * This 'returnUrl' can be used to redirect the user back to the page they were trying to access after they log in.
   *
   * @param {RouterStateSnapshot} state - The current router state.
   * @returns {Observable<boolean>} - An Observable that emits true if the user profile exists, false otherwise.
   */
  private nativeUwp(state: RouterStateSnapshot): Observable<boolean> {
    if (this.store.selectSnapshot(ProfileState.v2ProfileId)) {
      return of(true);
    }

    if (this.msalService.instance.getActiveAccount()) {
      return of(true);
    }

    this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });
    return of(false);
  }

  /**
   * This function is used to guard routes for web applications.
   * It waits until the MSAL (Microsoft Authentication Library) service is not in progress,
   * then checks if there are any accounts in the MSAL service.
   *
   * If there are accounts, it allows the navigation by returning an Observable of true.
   *
   * If there are no accounts, it redirects the user to the login page and returns an Observable of false.
   * The URL of the current state is passed as a query parameter 'returnUrl' to the login page.
   * This 'returnUrl' can be used to redirect the user back to the page they were trying to access after they log in.
   *
   * @param {RouterStateSnapshot} state - The current router state.
   * @returns {Observable<boolean>} - An Observable that emits true if there are accounts in the MSAL service, false otherwise.
   */
  private webGuard(state: RouterStateSnapshot): Observable<boolean> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      switchMap(() => {
        if (this.msalService.instance.getAllAccounts().length > 0) {
          return of(true);
        }

        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return of(false);
      }),
    );
  }
}
